import React, { useCallback } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';

import type { PreviewCardProps } from '../../../../common/types';
import { BasePreviewCard } from '../../../../common/ui/base-preview-card';

import type { jiraSingleWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraSingleWorkItemPreviewCard$key as JiraSingleWorkItemCardKey } from './__generated__/jiraSingleWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraSingleWorkItemPreviewCard.graphql';
import { PreviewContent } from './preview-content';

type JiraSingleWorkItemPreviewCardProps = PreviewCardProps & {
	queryRef: JiraSingleWorkItemCardKey;
	draftWorkItems: DraftWorkItem[];
	previewFooterActions?: React.ReactNode;
	onFieldChange: (
		invocationId: string,
		fieldId: string,
		newValue: unknown,
		skipFireAnalytics?: boolean,
	) => void;
	isCreated: boolean;
	openWorkItemView: (createdIssueKey?: string) => void;
	isStagingArea: boolean;
	messageId: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
};

const messages = defineMessages({
	previewHeaderTitle: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.preview-header-title',
		description: 'The title of the single work item preview card for the Jira AIFC experience',
		defaultMessage: 'Create work item',
	},
	dismissAction: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.dismiss',
		description:
			'The label for the button to dismiss the single work item preview card for the Jira AIFC experience',
		defaultMessage: 'Dismiss',
	},
	createAction: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create',
		description: 'The label for the button to create the work item for the Jira AIFC experience',
		defaultMessage: 'Create',
	},
	tempTaskTooltipLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.temp-task-tooltip-label',
		description: 'The label for the tooltip to create the work item for the Jira AIFC experience',
		defaultMessage: 'Task',
	},
});

const styles = cssMap({
	issueKeyContainer: {
		flexShrink: 0,
	},
});

const PreviewHeader = ({
	isCreated,
	primaryDraftWorkItem,
}: {
	isCreated: boolean;
	primaryDraftWorkItem?: DraftWorkItem;
}) => {
	return isCreated && primaryDraftWorkItem ? (
		<Inline alignBlock="center" space="space.075">
			<Inline xcss={styles.issueKeyContainer}>
				<Text size="medium" weight="regular" color="color.text.subtlest" maxLines={1}>
					{primaryDraftWorkItem.createdIssueKey ?? ''}
				</Text>
			</Inline>
			<Text size="medium" weight="medium" color="color.text" maxLines={1}>
				{primaryDraftWorkItem.summary}
			</Text>
		</Inline>
	) : FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
			fireExperimentExposure: false,
	  }) ? (
		<Heading as="h3" size="xsmall">
			<FormattedMessage {...messages.previewHeaderTitle} />
		</Heading>
	) : (
		<Text size="medium" weight="bold">
			<FormattedMessage {...messages.previewHeaderTitle} />
		</Text>
	);
};

/**
 * Renders the BasePreviewCard for the Jira AIFC experience when there is only one work item to create.
 *
 * @param cloudId - The cloud ID of the Jira instance.
 * @param isSelected - Whether the preview card is selected.
 * @param isEnabled - True when it is the last message in the conversation and the smart create modal is not open.
 * @param onCreate - The function to call when the create button is clicked.
 * @param onDismiss - The function to call when the dismiss button is clicked.
 * @param action - The action to render in the preview card.
 */
export const JiraSingleWorkItemPreviewCard = ({
	cloudId,
	queryRef,
	isSelected,
	isEnabled,
	isCreated,
	draftWorkItems,
	previewFooterActions,
	onFieldChange,
	openWorkItemView,
	isStagingArea,
	messageId,
	onProjectChange,
	sendAifcAnalyticsEvent,
}: JiraSingleWorkItemPreviewCardProps): React.JSX.Element | null => {
	const cardRef = useFragment<JiraSingleWorkItemCardKey>(
		graphql`
			fragment jiraSingleWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraSingleWorkItemPreviewCard on Query {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					key
					...previewContent_AtlassianConversationAssistantPreviewCard
				}
			}
		`,
		queryRef,
	);

	const projectKey = cardRef?.jira_projectByIdOrKey?.key;
	const primaryDraftWorkItem = draftWorkItems[0];

	const onCardClick = useCallback(() => {
		const createdIssueKey = isCreated ? primaryDraftWorkItem?.createdIssueKey : undefined;
		if (createdIssueKey) {
			openWorkItemView(createdIssueKey);
		}
	}, [isCreated, openWorkItemView, primaryDraftWorkItem]);

	if (!projectKey) {
		return null;
	}

	const hideFooterActions = isSelected || !isEnabled || isCreated;

	return (
		<BasePreviewCard
			testId="conversation-assistant-preview-card.jira-single-work-item-preview-card"
			previewProductLogo={
				<WorkItemIcon
					color={token('color.icon.accent.blue')}
					label={
						FeatureGates.getExperimentValue(
							'jira-aifc-a11y-fixes-preview-card',
							'isEnabled',
							false,
							{
								fireExperimentExposure: false,
							},
						)
							? ''
							: 'Work item'
					}
				/>
			}
			isSelected={isSelected}
			isEnabled={isEnabled}
			isCreated={isCreated}
			previewHeaderTitle={
				<PreviewHeader isCreated={isCreated} primaryDraftWorkItem={primaryDraftWorkItem} />
			}
			previewContent={
				primaryDraftWorkItem ? (
					<PreviewContent
						key={messageId}
						cloudId={cloudId}
						project={cardRef?.jira_projectByIdOrKey}
						workItem={primaryDraftWorkItem}
						projectKey={projectKey}
						onFieldChange={onFieldChange}
						isEditable={!!isEnabled}
						isCreated={isCreated}
						isStagingArea={isStagingArea}
						onProjectChange={onProjectChange}
						sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
					/>
				) : null
			}
			previewFooterActions={hideFooterActions ? null : previewFooterActions}
			onCardClick={onCardClick}
			isSingleWorkItem={true}
		/>
	);
};
